import { env } from "@/env";

export type DecisionJourneyType = "career" | "marriage";
export type DecisionJourneyGoal =
  | "job_search"
  | "promotion"
  | "business_launch"
  | "career_change"
  | "partner_search"
  | "compatibility_review"
  | "engagement_planning"
  | "marriage_preparation";
export type DecisionJourneyOutcome =
  | "on_track"
  | "blocked"
  | "changed_plan"
  | "achieved"
  | "no_update";

export interface AccountDecisionJourney {
  journey_id: string;
  reading_id: string;
  journey_type: DecisionJourneyType;
  goal_key: DecisionJourneyGoal;
  subject_id: string | null;
  stage: "clarity" | "preparation" | "action" | "review" | "complete";
  status: "active" | "completed" | "archived";
  target_date: string | null;
  state_version: number;
  latest_outcome_code: DecisionJourneyOutcome | null;
  latest_check_in_at: string | null;
  journey_label: string;
  goal_label: string;
  stage_label: string;
  progress_percent: number;
  next_action_key: string;
  next_action_label: string;
  next_action_detail: string;
  next_action_url: string | null;
}

export async function upsertDecisionJourney(
  token: string,
  input: {
    readingId: string;
    journeyType: DecisionJourneyType;
    goalKey: DecisionJourneyGoal;
    targetDate?: string;
    subjectId?: string;
    idempotencyKey: string;
  }
): Promise<AccountDecisionJourney> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/account/decision-journeys`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      reading_id: input.readingId,
      journey_type: input.journeyType,
      goal_key: input.goalKey,
      target_date: input.targetDate || null,
      subject_id: input.subjectId || null,
      idempotency_key: input.idempotencyKey,
    }),
  });
  if (res.status === 404) throw new Error("Choose a chart already verified in this account.");
  if (res.status === 409) throw new Error("That goal and saved chart cannot be combined.");
  if (!res.ok) throw new Error(`Could not save journey (HTTP ${res.status})`);
  return (await res.json()) as AccountDecisionJourney;
}

export async function checkInDecisionJourney(
  token: string,
  journeyId: string,
  outcomeCode: DecisionJourneyOutcome,
  idempotencyKey: string
): Promise<AccountDecisionJourney> {
  const res = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/account/decision-journeys/${encodeURIComponent(journeyId)}/check-in`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ outcome_code: outcomeCode, idempotency_key: idempotencyKey }),
    }
  );
  if (res.status === 404) throw new Error("This active journey is no longer available.");
  if (!res.ok) throw new Error(`Could not save check-in (HTTP ${res.status})`);
  return (await res.json()) as AccountDecisionJourney;
}

export async function archiveDecisionJourney(
  token: string,
  journeyId: string,
  idempotencyKey: string
): Promise<void> {
  const res = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/account/decision-journeys/${encodeURIComponent(journeyId)}/archive`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ idempotency_key: idempotencyKey }),
    }
  );
  if (res.status === 404) throw new Error("This active journey is no longer available.");
  if (res.status === 409) throw new Error("This archive action conflicts with an earlier retry.");
  if (!res.ok) throw new Error(`Could not archive journey (HTTP ${res.status})`);
}
